import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ago, formatBytes, useApi, usePoll, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Inspector, Loading, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

export interface Asset {
  id: string; name: string; kind: string; status: string; errorCode: string | null; errorMessage: string | null; tags: string[]; bytes: number;
  thumbnailUrl: string | null; playback: { width: number | null; height: number | null; durationMs: number | null; codec: string | null; mime: string } | null;
  rightsOwner: string | null; rightsValidUntil: string | null; createdAt: string; pageCount: number | null; isMusic: boolean;
}

interface Upload { name: string; progress: number; status: string; error?: string }

/** Chunked, resumable upload: chunks are exact-size and idempotent per index. */
export async function uploadFile(orgId: string, file: File, onProgress: (p: number) => void, extra: Record<string, unknown> = {}) {
  const start = await api<{ uploadId: string; assetId: string; chunkBytes: number; totalChunks: number }>('POST', `/v1/orgs/${orgId}/assets/uploads`, { name: file.name, bytes: file.size, mime: file.type, ...extra });
  const { ensureSession } = await import('../lib/auth.ts');
  const { API } = await import('../lib/api.ts');
  for (let i = 0; i < start.totalChunks; i++) {
    const chunk = file.slice(i * start.chunkBytes, (i + 1) * start.chunkBytes);
    for (let attempt = 0; ; attempt++) {
      const r = await fetch(`${API}/v1/orgs/${orgId}/assets/uploads/${start.uploadId}/chunks/${i}`, {
        method: 'PUT', headers: { authorization: `Bearer ${(await ensureSession())?.accessToken}`, 'content-type': 'application/octet-stream' }, body: chunk,
      }).catch(() => null);
      if (r?.ok) break;
      if (attempt >= 4) throw new Error(`Upload interrupted at part ${i + 1}; retry to resume`);
      await new Promise((res) => setTimeout(res, 500 * 2 ** attempt));
    }
    onProgress((i + 1) / start.totalChunks);
  }
  return api<{ assetId: string; status: string }>('POST', `/v1/orgs/${orgId}/assets/uploads/${start.uploadId}/complete`, {});
}

export function Library() {
  const org = useOrg();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const q = new URLSearchParams({ ...(kind ? { kind } : {}), ...(search ? { search } : {}) }).toString();
  const { data, error, loading, reload } = useApi<{ assets: Asset[] }>(orgPath(org, `/assets?${q}`), [org.id, q]);
  const processing = data?.assets.some((a) => ['uploading', 'quarantined', 'scanning', 'processing'].includes(a.status));
  usePoll(useCallback(() => void reload(), [reload]), 3000, !!processing);
  useEffect(() => {
    if (params.get('upload') === '1') input.current?.click();
  }, [params]);

  async function handle(files: FileList | null) {
    if (!files) return;
    for (const f of [...files]) {
      const idx = uploads.length;
      setUploads((u) => [...u, { name: f.name, progress: 0, status: 'uploading' }]);
      const set = (patch: Partial<Upload>) => setUploads((u) => u.map((x, i) => (i === idx || x.name === f.name ? { ...x, ...patch } : x)));
      try {
        await uploadFile(org.id, f, (p) => set({ progress: p }));
        set({ status: 'processing', progress: 1 });
        void reload();
      } catch (e) {
        set({ status: 'failed', error: (e as ApiFailure).message });
        toast(`${f.name}: ${(e as Error).message}`, 'danger');
        void reload();
      }
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Library</h1>
          <p>Images, video, audio and PDFs. Every upload is checked, scanned and converted to device-ready variants before it can be published.</p>
        </div>
        {can(org, 'assets.write') && <button className="btn btn-primary" onClick={() => input.current?.click()}><Icon name="upload" /> Upload</button>}
        <input ref={input} type="file" multiple hidden accept=".jpg,.jpeg,.png,.mp4,.mov,.m4v,.webm,.mp3,.m4a,.aac,.wav,.ogg,.flac,.pdf" onChange={(e) => void handle(e.target.files)} />
      </div>
      {can(org, 'assets.write') && (
        <div className={`dropzone${over ? ' over' : ''}`} style={{ marginBottom: 16 }} onDragOver={(e) => (e.preventDefault(), setOver(true))} onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); void handle(e.dataTransfer.files); }}>
          Drop files here · JPEG, PNG, MP4/MOV/WebM, MP3/M4A/WAV, PDF · up to 2 GB
        </div>
      )}
      {!!uploads.length && (
        <div className="card card-pad stack" style={{ marginBottom: 16 }} aria-live="polite">
          {uploads.map((u, i) => (
            <div key={i} className="stack" style={{ gap: 4 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}><span>{u.name}</span><Status value={u.status} /></div>
              <div className="progress" role="progressbar" aria-valuenow={Math.round(u.progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`${u.name} upload`}><span style={{ width: `${u.progress * 100}%` }} /></div>
              {u.error && <span className="subtle" style={{ color: 'var(--danger)' }}>{u.error}</span>}
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ marginBottom: 16 }}>
        <input className="input" style={{ maxWidth: 260 }} placeholder="Search media" aria-label="Search media" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="segmented" role="group" aria-label="Type">
          {['', 'image', 'video', 'audio', 'pdf'].map((k) => <button key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>{k || 'All'}</button>)}
        </div>
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && !data.assets.length && <div className="card"><Empty title="Your library is empty">Upload images, video, music or a PDF to get started.</Empty></div>}
      <div className="grid grid-cards">
        {data?.assets.map((a) => (
          <button key={a.id} className="card" style={{ textAlign: 'left', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit' }} onClick={() => setParams({ asset: a.id })}>
            <div className="thumb" style={{ borderRadius: '16px 16px 0 0' }}>
              {a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" loading="lazy" /> : <Icon name={a.kind === 'audio' ? 'music' : 'library'} size={32} />}
            </div>
            <div style={{ padding: 14 }} className="stack">
              <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</strong>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="subtle">{a.kind}{a.playback?.width ? ` · ${a.playback.width}×${a.playback.height}` : ''}{a.playback?.durationMs ? ` · ${(a.playback.durationMs / 1000).toFixed(1)}s` : ''}{a.pageCount ? ` · ${a.pageCount} pages` : ''}</span>
                <Status value={a.status} />
              </div>
              {a.errorMessage && <span className="subtle" style={{ color: 'var(--danger)' }}>{a.errorMessage}</span>}
            </div>
          </button>
        ))}
      </div>
      {params.get('asset') && <AssetInspector id={params.get('asset')!} onClose={() => setParams({})} onChanged={reload} />}
    </>
  );
}

function AssetInspector({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const { data, error, reload } = useApi<{ asset: Record<string, unknown>; variants: { id: string; purpose: string; page: number | null; mime: string; width: number | null; height: number | null; bytes: number; url: string; codec: string | null }[]; versions: { sha256: string; provenance: Record<string, unknown>; createdAt: string }[]; references: { playlists: { name: string }[]; activeReleases: { screenName: string; sequence: number }[] } }>(orgPath(org, `/assets/${id}`), [id]);
  const [owner, setOwner] = useState('');
  const [until, setUntil] = useState('');
  useEffect(() => {
    if (data) {
      setOwner((data.asset.rights_owner as string) ?? '');
      setUntil(data.asset.rights_valid_until ? String(data.asset.rights_valid_until).slice(0, 16) : '');
    }
  }, [data]);
  const playback = data?.variants.find((v) => v.purpose === 'playback' || v.purpose === 'page');
  async function saveRights() {
    try {
      await api('PATCH', orgPath(org, `/assets/${id}`), { rights: { owner: owner || null, validUntil: until ? new Date(until).toISOString() : null } });
      toast('Rights saved. Players stop showing this item when rights expire, even offline.');
      void reload();
      onChanged();
    } catch (e) {
      toast((e as Error).message, 'danger');
    }
  }
  async function remove() {
    try {
      await api('DELETE', orgPath(org, `/assets/${id}`));
      toast('Deleted');
      onChanged();
      onClose();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <Inspector title={(data?.asset.name as string) ?? 'Media'} onClose={onClose}>
      {error && <ErrorState error={error} />}
      {!data ? <Loading /> : (
        <div className="stack" style={{ gap: 18 }}>
          {playback && (playback.mime.startsWith('image/') ? <img src={playback.url} alt={`Preview of ${String(data.asset.name)}`} style={{ width: '100%', borderRadius: 12 }} />
            : playback.mime.startsWith('video/') ? <video src={playback.url} controls muted style={{ width: '100%', borderRadius: 12 }} />
            : <audio src={playback.url} controls style={{ width: '100%' }} />)}
          <span className="subtle">Rendered preview of the processed file.</span>
          <dl className="kv">
            <dt>Status</dt><dd><Status value={data.asset.status as string} /></dd>
            <dt>Uploaded</dt><dd>{ago(data.asset.created_at as string)}</dd>
            <dt>Checksum</dt><dd className="mono">{data.versions[0]?.sha256?.slice(0, 16)}…</dd>
            <dt>Scan</dt><dd>{((data.versions[0]?.provenance?.scan as { engines?: string[] })?.engines ?? []).join(', ') || '—'}</dd>
            {data.variants.map((v) => (<Fragment key={v.id}><dt>{v.purpose}{v.page ? ` ${v.page}` : ''}</dt><dd>{v.mime}{v.codec ? ` (${v.codec})` : ''} · {v.width ? `${v.width}×${v.height} · ` : ''}{formatBytes(v.bytes)}</dd></Fragment>))}
          </dl>
          <section className="stack">
            <h3>Rights</h3>
            <div className="field"><label htmlFor="r-owner">Rights owner</label><input id="r-owner" className="input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Who licenses this content" /></div>
            <div className="field"><label htmlFor="r-until">Valid until</label><input id="r-until" type="datetime-local" className="input" value={until} onChange={(e) => setUntil(e.target.value)} /></div>
            {can(org, 'assets.write') && <button className="btn" onClick={() => void saveRights()}>Save rights</button>}
          </section>
          <section className="stack">
            <h3>Used by</h3>
            {data.references.playlists.length + data.references.activeReleases.length === 0 ? <span className="subtle">Not used yet.</span> : (
              <>
                {data.references.playlists.map((p) => <span key={p.name}>Playlist · {p.name}</span>)}
                {data.references.activeReleases.map((r) => <span key={r.screenName}>Screen · {r.screenName} (release #{r.sequence})</span>)}
              </>
            )}
          </section>
          {can(org, 'assets.delete') && <button className="btn btn-danger" onClick={() => void remove()}><Icon name="trash" /> Delete</button>}
        </div>
      )}
    </Inspector>
  );
}
